<template>
  <div class="card">
    <div class="card-body">
      <!-- #region Request and Refresh buttons -->
      <div class="d-flex align-items-center py-2">
        <div>
          <h5>{{ eloadMaintenanceTotal }} Record(s)</h5>
        </div>
        <div class="ms-auto">
          <router-link :to="`maintenance/0`">
            <button class="btn btn-primary me-2 mb-2"><i class="uil uil-plus me-1"></i>Schedule Maintenance</button>
          </router-link>
          <button class="btn btn-secondary mb-2" @click="getMaintenanceList()">
            <feather type="refresh-ccw" size="16" class="icon nav-icon"></feather>&nbsp;&nbsp; Refresh
          </button>
        </div>
      </div>
      <!-- #endregion Request and Refresh buttons -->

      <!-- #region Search and Filter -->
      <div class="row p-2">
        <div class="col-md-4 col-sm-12 p-0">
          <div class="ta-right me-2">
            <b-form-group description="Search Keyword">
              <form class="d-flex" @submit.prevent="">
                <b-form-group id="input-group-3" label-for="search-field">
                  <b-form-select
                    style="width: fit-content"
                    class="form-select rounded-0 rounded-start shadow-none"
                    id="search-field"
                    v-model="search.field"
                    :options="searchFieldOptions"
                  ></b-form-select>
                </b-form-group>

                <b-input-group>
                  <b-form-input class="rounded-0 rounded-end" v-model="search.keyword"></b-form-input>
                </b-input-group>
              </form>
            </b-form-group>
          </div>
        </div>

        <div class="col-md-2 col-sm-12 p-0">
          <div class="ta-right me-2">
            <b-form-group description="Filter by">
              <b-form-select
                class="form-select rounded shadow-none dd-custom-size"
                v-model="search.filter"
                :options="filterByOptions"
                @input="setFilter"
              ></b-form-select>
            </b-form-group>
          </div>
        </div>

        <div class="col-md-2 col-sm-12 p-0" v-if="showFilterInput">
          <div class="ta-right me-2">
            <b-form-group :description="filterDescription" class="me-2 w-daterange">
              <b-form-select
                v-if="search.filter == 'provider'"
                class="form-select rounded-0 rounded-start shadow-none w-100"
                v-model="search.filterKeyword"
                :options="providerOptions"
              ></b-form-select>
              <b-form-select
                v-else-if="search.filter == 'network'"
                class="form-select rounded-0 rounded-start shadow-none w-100"
                v-model="search.filterKeyword"
                :options="networkOptions"
              ></b-form-select>
              <b-form-select
                v-else-if="search.filter == 'network_group'"
                class="form-select rounded-0 rounded-start shadow-none w-100"
                v-model="search.filterKeyword"
                :options="networkGroupOptions"
              ></b-form-select>
              <flat-pickr
                v-else-if="search.filter == 'start_at'"
                v-model="search.dateRange"
                :config="rangeOptions"
                placeholder="Select a date range"
                class="form-control"
              ></flat-pickr>
              <flat-pickr
                v-else-if="search.filter == 'end_at'"
                v-model="search.dateRange"
                :config="rangeOptions"
                placeholder="Select a date range"
                class="form-control"
              ></flat-pickr>
            </b-form-group>
          </div>
        </div>

        <div class="col-md-4 col-sm-12 d-flex">
          <div class="me-2">
            <b-button
              variant="primary"
              class="h-fit d-flex align-items-center justify-content-center me-2 w-fit"
              @click="getMaintenanceList"
            >
              <i class="bx bx-search me-1 mt-1"></i>Search
            </b-button>
          </div>
          <div class="">
            <b-button
              variant="primary"
              class="h-fit me-2 d-flex align-items-center justify-content-center w-fit"
              @click="
                isSearching = true;
                onExport();
              "
            >
              <i class="uil uil-export me-1"></i>
              Export
            </b-button>
          </div>
        </div>
      </div>
      <!-- #endregion Search and Filter -->

      <!-- #region Table -->
      <div class="table-responsive">
        <div class="table align-middle table-nowrap">
          <b-table
            :items="eloadMaintenanceList"
            :fields="fields"
            responsive="sm"
            :current-page="currentPage"
            :filter-included-fields="filterOn"
            class="table-check"
            @sort-changed="sortList"
            tbody-tr-class="align-middle"
          >
            <template #cell(action)="data">
              <!-- TODO: Add checking if approver -->
              <button type="button" class="btn btn-sm btn-secondary cancelled-button" v-if="data.item.status=='Scheduled'" @click="cancelSchedule('cancelled', data.item.id)">
                <i class="mdi mdi-calendar-remove"></i>
              </button>
              <button type="button" class="btn btn-sm btn-secondary cancelled-button"  v-if="data.item.status=='Ongoing'" @click="cancelSchedule('force-cancelled',data.item.id)">
                <i class="mdi mdi-close-circle-outline"></i>
              </button>
              <router-link :to="`maintenance/${data.item.id}`">
                <button type="button" class="btn btn-sm btn-secondary">
                  <i class="mdi mdi-eye-outline"></i>
                </button>
              </router-link>
              
            </template>
            <template #cell(type)="data">
              <div style="text-transform:capitalize;">{{ data.item.type }}</div>
            </template>

            <template #cell(status)="data">
              <div
                :class="{
                  'text-scheduled': data.item.status == 'Scheduled',
                  'text-success': data.item.status == 'Ongoing',
                  'text-ended': data.item.status == 'Ended',
                  'text-danger': data.item.status == 'Cancelled' || data.item.status == 'Force Cancelled'
                }"
              >
                {{ data.item.status }}
              </div>
            </template>

            <template #cell(provider)="data">
              <span v-for="item in splitArray(data.item.provider)" :key="item" class="badge bg-light me-1">{{ (data.item.type=='provider') ? item: '' }}</span>
              <span v-if="data.item.type!='provider'" class="badge bg-light me-1">--</span>
            </template>

            <template #cell(network)="data">
              <span v-for="item in splitArray(data.item.network)" :key="item" class="badge bg-light me-1">{{ (data.item.type=='network') ? item : '' }}</span>
              <span v-if="data.item.type!='network'" class="badge bg-light me-1">--</span>
              <!--<div>{{ data.item.network }}</div>-->
            </template>

            <!-- <template #cell(network_group)="data">
              <span v-for="item in splitArray(data.item.network_group)" :key="item" class="badge bg-light me-1">{{ item }}</span>
            </template>-->

            <template #cell(start_at)="data">
              <div>{{ data.item.start_at | formatDate }}</div>
            </template>

            <template #cell(end_at)="data">
              <div>{{ data.item.end_at | formatDate }}</div>
            </template>

            

            <template #cell(created_by)="data">
              <div>{{ data.item.created_by }}</div>
            </template>
          </b-table>
        </div>
      </div>
      <div class="row g-0 text-center" v-if="eloadMaintenanceTotal == 0">
        <span>No data to display.</span>
      </div>
      <!-- #endregion Table -->

      <!-- #region Pagination -->
      <div class="row g-0 text-center text-sm-start" v-if="eloadMaintenanceTotal != 0 && eloadMaintenanceTotal > 10">
        <div class="col-sm-12">
          <div class="row">
            <div class="col">
              <div class="form-group row pull-left">
                <label for="staticEmail" class="col-sm-6 col-form-label">Display:</label>
                <div class="col-sm-6">
                  <b-form-group label-for="display-record">
                    <b-form-select
                      class="form-select shadow-none w-fit"
                      id="display-record"
                      v-model="perPage"
                      :options="pageOptions"
                      @change="getMaintenanceList()"
                    ></b-form-select>
                  </b-form-group>
                </div>
              </div>
              <div class="dataTables_paginate paging_simple_numbers float-end" v-if="eloadMaintenanceTotal > perPage">
                <b-pagination
                  class="pagination-rounded"
                  v-model="currentPage"
                  @input="getMaintenanceList()"
                  :total-rows="eloadMaintenanceTotal"
                  :per-page="perPage"
                ></b-pagination>
              </div>
            </div>
          </div>
        </div>
        <!-- end col -->
      </div>
      <!-- #endregion Pagination -->
    </div>
  </div>
</template>

<script>
import { layoutMethods, exportMethods, erpFixMessages, erpUserRolesMethods, coreEloadMaintenanceMethods, coreEloadDropdownComputed, coreEloadDropdownMethods } from '@/state/helpers';
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import Swal from "sweetalert2";
// import { getModuleAccess } from "@/router/helper.js";
import moment from "moment";

export default {
  page: {
    title: "ELoad Maintenance Dashboard",
  },
  data() {
    return {
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
      filter: null,
      filterOn: [],
      sortBy: "rml.id",
      sort: "DESC",
      sortDesc: false,
      submitted: false,
      search: {
        keyword: "",
        field: "",
        status: "",
        dateRange: null,
        filter: "",
        filterKeyword: "",
      },
      rangeOptions: {
        mode: "range",
      },
      searchFieldOptions: [
        { text: "All", value: "" },
        { text: "Added By", value: "created_by" },
      ],
      filterByOptions: [
        { text: "Select Filter", value: "" },
        { text: "Provider", value: "provider" },
        { text: "Network", value: "network" },
        // { text: "Network Group", value: "network_group" },
        { text: "Start Date", value: "start_at" },
        { text: "End Date", value: "end_at" },
      ],
      fields: [
        { key: "action", sortable: false },
        { key: "type", label: "Maintenance Type", sortable: true },
        { key: "status", sortable: true },
        { key: "start_at", label: "Maintenance Start Date", sortable: true },
        { key: "end_at", label: "Maintenance End Date", sortable: true },
        { key: "provider", sortable: true },
        { key: "network", sortable: true },
        // { key: "network_group", sortable: true },
        { key: "created_by", label: "Added By", sortable: true },
      ],
      userAccess: {
        access: {
          create: false,
          read: false,
          update: false,
          delete: false,
        },
      },
      filterDescription: "",
      showFilterInput: false,
      filterKeywordOptions: [],
      eloadMaintenanceList: [],
      eloadMaintenanceTotal:0
    };
  },
  components: {
    flatPickr,
  },
  computed: {
    ...erpFixMessages,
    ...coreEloadDropdownComputed,
    providerOptions() {
      console.log('a',this.providersList)
      return this.getCoreProvidersList.map((item) => {
        return {
          text: item.name,
          value: item.id,
        };
      });
    },
    networkOptions() {
      return this.getCoreNetworksList.map((item) => {
        return {
          text: item.name,
          value: item.id,
        };
      });
    },
  },
  methods: {
    ...layoutMethods,
    ...exportMethods,
    ...erpUserRolesMethods,
    ...coreEloadMaintenanceMethods,
    ...coreEloadDropdownMethods,
    async getMaintenanceList() {
      try {
        let response = await this.actionCoreEloadMaintenanceAPIPOST({
          params: {
            page: this.isSearching ? 1 : this.currentPage,
            rows: this.perPage,
            sort: this.sort,
            orderBy: this.sortBy,
            search: this.search,
          },
          action: 'list-maintenance'
        })
        if(response.status == 'ok'){
          this.eloadMaintenanceList = response.message.rows;
          this.eloadMaintenanceTotal = response.message.pageInfo.total;
        }
      } catch(error){
        console.error('Error fetching maintenance list:', error);
        this.$alertify.error('Failed to fetch maintenance list');
      }
    },
    async cancelSchedule(scheduleStatus,scheduleId) {
      let messageTitle = '', messageText = ''
      if(scheduleStatus=='cancelled') {
        messageTitle = 'Cancel Maintenance'
        messageText = 'Are you sure you want to cancel this scheduled maintenance? This action will stop the maintenance before it begins.'
      }
      if(scheduleStatus=='force-cancelled'){
        messageTitle = 'Force Cancel Maintenance'
        messageText = 'Are you sure you want to force cancel this maintenance? Any ongoing processes will be stopped immediately.'
      }  
      Swal.fire({
              title: messageTitle,
              text: messageText,
              icon: "warning",
              showCancelButton: true,
              confirmButtonColor: "#34c38f",
              cancelButtonColor: "#f46a6a",
              confirmButtonText: "Yes",
              cancelButtonText: 'No'
          }).then( async(result) => {
            if(result.isConfirmed) {
              let payload = {
                id: scheduleId,
                scheduleStatus: scheduleStatus,
                updateStatus: true
              };
              let response = await this.actionCoreEloadMaintenanceAPIPOST({
                params: payload,
                action: 'update-maintenance'
              })
              if(response.status == 'ok'){
                Swal.fire("Maintenance Cancelled", "The scheduled maintenance has benn successfully cancelled.", "success").then((swalRes) => {
                    if (swalRes.isConfirmed) {
                      this.getMaintenanceList();
                    }
                  });
              }
            } else {
              return;
            }
          })
    },
    setStatus(maintenance) {
      let status = "";
      let today = moment();
      
      if (maintenance.active == 0) {
        status = "Inactive"
      } else if (today.isBefore(moment(maintenance.start_at))) {
        status = "Scheduled"
      } else if (today.isSameOrAfter(moment(maintenance.start_at)) && today.isSameOrBefore(moment(maintenance.end_at))) {
        status = "Ongoing"
      } else if (today.isAfter(moment(maintenance.end_at))) {
        status = "Completed"
      }
      return status;
    },
    async sortList(action) {
      var column = "";
      switch (action.sortBy) {
        case "provider":
          column = "provider";
          break;
        case "network":
          column = "network";
          break;
        case "start_at":
          column = "rml.start_at";
          break;
        case "end_at":
          column = "rml.end_at";
          break;
        case "type":
          column = "rml.type";
          break;
        case "created_by":
          column = "rml.created_by";
          break;
        case "status":
          column = "status";
          break;
        default:
          column = `rml.id`;
          break;
      }
      this.sortBy = column;
      this.sort = action.sortDesc ? "DESC" : "ASC";
      await this.getMaintenanceList();
    },
    async onExport() {
      await this.getMaintenanceList();
        if(this.eloadMaintenanceTotal == 0){
          Swal.fire("Failed!", this.exportNullPrompt, "warning");
          return;
        }
      let params = {
        email: JSON.parse(localStorage.getItem("userData")).email,
        search: this.search,
        sort: this.sort,
        orderBy: this.sortBy,
      };
      this.actionCoreEloadMaintenanceAPIPOST({
        params: params,
        action: "export"
      })
      .then(() => Swal.fire("Success!", `Your request for reports is pending. It will be sent to your email once it's ready.`, "success"))
      .catch(() => Swal.fire("Failed!", `Your request for reports has failed.`, "warning"));
    },
    splitArray(items) {
      if (items) {
        return items.split(",");
      }
    },
    setProviders(items) {
      if (items) {
        const providerIds = items.split(",");
        let providers = [];
        for (let id of providerIds) {
          providers.push(this.eloadMaintenanceProvider.find((i) => i.id == id).name);
        }
        return providers;
      }
    },
    setFilter() {
      switch (this.search.filter) {
        case "provider":
          this.filterDescription = "Filter by Provider";
          this.showFilterInput = true;
          break;
        case "network":
          this.filterDescription = "Filter by Network";
          this.showFilterInput = true;
          break;
        case "network_group":
          this.filterDescription = "Filter by Network Group";
          this.showFilterInput = true;
          break;
        case "start_at":
          this.filterDescription = "Filter by Start Date";
          this.showFilterInput = true;
          break;
        case "end_at":
          this.search.filterKeyword = "";
          this.filterDescription = "Filter by End Date";
          this.showFilterInput = true;
          break;
        default:
          this.showFilterInput = false;
          break;
      }
    },
  },
  async mounted() {
    this.changeTitle({ title: "eLoad Maintenance" });
    this.changeBreadCrumb({
      breadCrumbs: [
        { text: "Peddlr", to: { name: "Home" } },
        { text: 'E-Load Core Manager', to: { name: 'E-Load Core Maintenance'} },
        { text: "E-Load Maintenance", to: { name: "" } },
      ],
    });
    await this.fetchCoreNetworksList();
    await this.fetchCoreProvidersList();
    await this.getMaintenanceList();
    // await getModuleAccess("Products", "eLoad B2B", "Request").then((access) => (this.userAccess.access = access));
  },
};
</script>

<style scoped>
.requesting-color-code {
  color: #dc7512 !important;
}

.img-thumbnail {
  max-height: 50px !important;
}

.disabled {
  pointer-events: none;
  color: gray;
  cursor: not-allowed;
}
.text-ended {
  color: #AFAFAF;
}
.text-scheduled {
  color: #4D56FF;
}
.cancelled-button, .cancelled-button {
  margin-right:10px;
  background-color: #C12927;
  border-color: #C12927;
}
</style>
